import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  ChevronDown,
  Columns3,
  ExternalLink,
  PaintBucket,
} from 'lucide-react'
import type { Device } from '@/types/device'
import {
  MACHINE_COLORS,
  SOFTWARE_VERSION_FIELDS,
  SOFTWARE_VERSION_KEYS,
  type ComponentCategory,
  type ComponentDef,
  type Machine,
  type MachineColor,
  type MachineColorPatch,
  type MachineColors,
} from '@/types/machine'
import { isHttpUrl } from '@/lib/utils'
import { MarkdownView } from '@/components/MarkdownField'
import Highlight from '@/components/Highlight'

export interface MachineRow {
  key: string
  machine: Machine | null
  device: Device | null
  // 台帳未登録デバイス行のマーカー色（登録済み行は machine 側の色を使う）
  deviceColors?: MachineColors | null
}

// 行のマーカー色の読み出し元（台帳登録済みは機材レコード、未登録はデバイス色）
function rowColors(row: MachineRow): MachineColors | undefined {
  return row.machine ?? row.deviceColors ?? undefined
}

// 色付け1対象分の更新内容（選択範囲が複数の機材/デバイスにまたがると複数件になる）。
// 台帳登録済みは machine、未登録の IoT デバイスは deviceName を指定する
export interface PaintUpdate {
  machine?: Machine
  deviceName?: string
  patch: MachineColorPatch
}

// 実行した色付け操作。ツールバーの塗りつぶしボタンが「前回の色」として覚える
export interface PaintAction {
  type: 'cell' | 'row'
  color: MachineColor | null
}

// ツールバーから選択範囲へ色を付けるための命令的ハンドル
export interface MachineTableHandle {
  paintSelection: (type: 'cell' | 'row', color: MachineColor | null) => void
}

// セル選択の1セル分の参照。行・列とも並び替えの影響を受けないキーで持つ
interface CellRef {
  rowKey: string
  colKey: string
}

// アンカー（選択開始セル）とフォーカス（現在セル）が張る矩形範囲
interface SelRange {
  anchor: CellRef
  focus: CellRef
}

// 列キーには 'comp:<部品名>' など任意の文字が入るため区切りに NUL を使う
const cellId = (cell: CellRef) => `${cell.rowKey}\u0000${cell.colKey}`

interface MachineTableProps {
  rows: MachineRow[]
  // 部品マスタ。構成部品の列はここから動的に作る
  componentDefs: ComponentDef[]
  visibleColumns: string[]
  // 検索文字列。セル中の一致箇所をハイライトする
  highlight?: string
  // ソート状態。一括編集・CSVでも同じ並び順を使えるよう親が持つ
  sortKey: string
  sortAsc: boolean
  onSortChange: (key: string, asc: boolean) => void
  onSelect: (row: MachineRow) => void
  // マーカー色の変更（選択中のセル/行へまとめて適用）。右クリックメニューと
  // ツールバーの塗りつぶしボタンの両方から呼ばれる。未指定なら色付け不可
  onPaint?: (updates: PaintUpdate[], action: PaintAction) => void
  // 色を付けられる選択セル数の変化を通知する（塗りつぶしボタンの活性制御用）
  onSelectionChange?: (paintableCellCount: number) => void
}

// マーカー色 → 行・セルの背景クラス。動的生成だと Tailwind にパージされる
// ため静的なマップで持つ
const COLOR_BG: Record<string, string> = {
  red: 'bg-red-100',
  orange: 'bg-orange-100',
  yellow: 'bg-yellow-100',
  green: 'bg-green-100',
  blue: 'bg-blue-100',
  purple: 'bg-purple-100',
  gray: 'bg-gray-200',
}

// パレットの色見本は背景より濃くして選びやすくする
const COLOR_SWATCH: Record<MachineColor, string> = {
  red: 'bg-red-400',
  orange: 'bg-orange-400',
  yellow: 'bg-yellow-300',
  green: 'bg-green-400',
  blue: 'bg-blue-400',
  purple: 'bg-purple-400',
  gray: 'bg-gray-400',
}

interface ColumnDef {
  key: string
  // labelKey は i18n キー。label はそのまま表示する文字列（ソフトバージョン列など）
  labelKey?: string
  label?: string
  // 長文（複数行）になり得る列は1行に切り詰めて表示する
  longText?: boolean
  // 固定レイアウト時の初期列幅（未指定は longText の有無で決める）
  width?: number
  value: (row: MachineRow) => string
}

// シリアルを先頭に、以降は生産管理台帳（PDF）の列順に合わせて定義する
// （表示項目セレクタもこの順で並ぶ）
const BASE_COLUMNS: ColumnDef[] = [
  {
    key: 'serial',
    labelKey: 'machines.serial',
    value: (r) =>
      r.machine
        ? (r.machine.serialNumber ?? r.machine.deviceName ?? '')
        : (r.device?.deviceName ?? ''),
  },
  {
    key: 'lot',
    labelKey: 'detail.lot',
    value: (r) => r.machine?.lot ?? '',
  },
  {
    key: 'customer',
    labelKey: 'machines.customer',
    value: (r) => r.machine?.customer ?? r.device?.siteId ?? '',
  },
  {
    key: 'edition',
    labelKey: 'machines.edition',
    value: (r) => r.machine?.edition ?? '',
  },
  {
    key: 'shippedAt',
    labelKey: 'machines.shippedAt',
    value: (r) => r.machine?.shippedAt ?? '',
  },
  {
    key: 'supportStatus',
    labelKey: 'machines.supportStatus',
    longText: true,
    value: (r) => r.machine?.supportStatus ?? '',
  },
  {
    key: 'bodySerial',
    labelKey: 'machines.bodySerial',
    value: (r) => r.machine?.bodySerial ?? '',
  },
  {
    key: 'bodyRecord',
    labelKey: 'detail.bodyRecord',
    // リンク遷移アイコンだけ表示するため狭くてよい
    width: 100,
    value: (r) => r.machine?.bodyRecord ?? '',
  },
  {
    key: 'bodyInspectionReport',
    labelKey: 'detail.bodyInspectionReport',
    width: 100,
    value: (r) => r.machine?.bodyInspectionReport ?? '',
  },
  ...SOFTWARE_VERSION_FIELDS.map(({ key, label }) => ({
    key,
    label,
    value: (r: MachineRow) => r.machine?.softwareVersions?.[key] ?? '',
  })),
  {
    key: 'remarks',
    labelKey: 'detail.remarks',
    longText: true,
    value: (r) => r.machine?.remarks ?? '',
  },
  {
    key: 'deviceName',
    labelKey: 'detail.deviceName',
    value: (r) => r.machine?.deviceName ?? r.device?.deviceName ?? '',
  },
  {
    key: 'updatedAt',
    labelKey: 'machines.updatedAt',
    value: (r) => r.machine?.updatedAt?.slice(0, 10) ?? '',
  },
  {
    key: 'certificate',
    labelKey: 'machines.certificate',
    value: (r) => r.device?.certificates[0]?.status ?? '',
  },
]

// 構成部品も列として選択できるようにする（値は S/N とメモを併記）
const componentColumn = ({ name }: { name: string }): ColumnDef => ({
  key: `comp:${name}`,
  label: name,
  longText: true,
  value: (r) => {
    const component = r.machine?.components?.find((c) => c.name === name)
    return [component?.serialNumber, component?.note].filter(Boolean).join('\n')
  },
})

// コントロールボックスの部品列とその他の部品列を分けて並べる
function componentColumns(
  defs: ComponentDef[],
  category: ComponentCategory
): ColumnDef[] {
  return defs.filter((c) => c.category === category).map(componentColumn)
}

// シリアルを先頭に、以降は生産管理台帳（PDF）と同じ列順にする。構成部品は
// コントロールボックス部品→その他の部品の順で、各区分内は部品マスタの
// 並び順（部品管理画面と同じ）。台帳にない列（デバイス名・最終更新・証明書）は末尾
function buildColumns(defs: ComponentDef[]): ColumnDef[] {
  const byKey = new Map(BASE_COLUMNS.map((c) => [c.key, c]))
  const pick = (keys: string[]) =>
    keys.map((k) => byKey.get(k)).filter((c): c is ColumnDef => Boolean(c))
  return [
    ...pick([
      'serial',
      'lot',
      'customer',
      'edition',
      'shippedAt',
      'supportStatus',
      'bodySerial',
      'bodyRecord',
      'bodyInspectionReport',
    ]),
    ...componentColumns(defs, 'controlBox'),
    ...componentColumns(defs, 'other'),
    ...pick([
      ...SOFTWARE_VERSION_KEYS,
      'remarks',
      'deviceName',
      'updatedAt',
      'certificate',
    ]),
  ]
}

export function allColumnKeys(defs: ComponentDef[]): string[] {
  return buildColumns(defs).map((column) => column.key)
}

const STORAGE_KEY = 'device-manager.machine-table.columns'
const WIDTH_STORAGE_KEY = 'device-manager.machine-table.widths'

// 列幅ドラッグ時の最小幅と、ダブルクリックフィット時の最大幅（px）
const MIN_COL_WIDTH = 60
const MAX_FIT_WIDTH = 480
const DETAIL_COL_KEY = '__detail'

// 「シリアル」〜「タイプ」の列は横スクロールしても左に固定する
const FROZEN_COLUMN_KEYS = ['serial', 'lot', 'customer', 'edition']
// リンクを登録する列。値がURLならクリックで開けるアンカーとして表示する
const LINK_COLUMN_KEYS = new Set(['bodyRecord', 'bodyInspectionReport'])
// 幅指定のある列（アイコンだけのリンク列など）のキー
const FIXED_WIDTH_COL_KEYS = BASE_COLUMNS.filter((c) => c.width != null).map(
  (c) => c.key
)

function defaultColWidth(column: ColumnDef): number {
  return column.width ?? (column.longText ? 224 : 150)
}

function loadColWidths(): Record<string, number> {
  try {
    const saved = JSON.parse(localStorage.getItem(WIDTH_STORAGE_KEY) ?? '')
    if (
      saved &&
      typeof saved === 'object' &&
      !Array.isArray(saved) &&
      Object.values(saved).every((v) => typeof v === 'number')
    ) {
      // 詳細ボタン列は保存値を使わず毎回内容にフィットさせる（パディング等の
      // 変更後に古い幅が残り続けるのを防ぐ）
      delete saved[DETAIL_COL_KEY]
      // 幅指定のある列も保存値より指定幅を優先する（表示内容の変更前に
      // スナップショットされた古い幅が残り続けるのを防ぐ）
      for (const key of FIXED_WIDTH_COL_KEYS) delete saved[key]
      return saved
    }
  } catch {
    // 保存なし・破損時はデフォルト（内容に応じた自動幅）
  }
  return {}
}

// 表示列の保存形式は { visible, known }。known に無い列 = 保存後に部品マスタへ
// 追加された列とみなし、自動で表示に含める（部品の追加を一覧へ反映するため）
export function loadVisibleColumns(allKeys: string[]): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '')
    // 旧形式（表示列の配列のみ）は known の情報がないためそのまま使う
    if (Array.isArray(saved)) {
      return saved.filter((k: string) => allKeys.includes(k))
    }
    if (saved && Array.isArray(saved.visible) && Array.isArray(saved.known)) {
      const newKeys = allKeys.filter((k) => !saved.known.includes(k))
      return [
        ...saved.visible.filter((k: string) => allKeys.includes(k)),
        ...newKeys,
      ]
    }
  } catch {
    // 保存なし・破損時はデフォルト（全列表示）
  }
  return allKeys
}

function CertBadge({ row }: { row: MachineRow }) {
  const { t } = useTranslation()
  const cert = row.device?.certificates[0]
  if (cert) {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          cert.status === 'ACTIVE'
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-600'
        }`}
        title={cert.certificateId}
      >
        {cert.status}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      {t('machines.certNone')}
    </span>
  )
}

// 色パレット1列分（7色 + 色消し）。current の色には選択中リングを付ける
function ColorSwatches({
  current,
  clearLabel,
  onPick,
}: {
  current?: string
  clearLabel: string
  onPick: (color: MachineColor | null) => void
}) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5">
      {MACHINE_COLORS.map((color) => (
        <button
          key={color}
          onClick={() => onPick(color)}
          className={`h-5 w-5 rounded-full border transition-shadow ${
            COLOR_SWATCH[color]
          } ${
            current === color
              ? 'border-transparent ring-2 ring-primary-500 ring-offset-1'
              : 'border-gray-300 hover:ring-2 hover:ring-gray-300'
          }`}
        />
      ))}
      <button
        onClick={() => onPick(null)}
        title={clearLabel}
        className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-400 hover:text-gray-600"
      >
        <Ban className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ツールバーに置く表示項目セレクタ（テーブル本体とは分離して配置できる）
export function ColumnPicker({
  componentDefs,
  visible,
  onToggle,
  onSet,
}: {
  componentDefs: ComponentDef[]
  visible: string[]
  onToggle: (key: string) => void
  // すべて表示/非表示など、表示列リストの一括置き換え
  onSet: (keys: string[]) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const controlBoxColumns = componentColumns(componentDefs, 'controlBox')
  const otherColumns = componentColumns(componentDefs, 'other')

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        title={t('machines.columns')}
      >
        <Columns3 className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-20 w-56 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg py-2">
            <div className="flex items-center gap-3 px-3 pt-1 pb-2 border-b border-gray-100">
              <button
                onClick={() => onSet(allColumnKeys(componentDefs))}
                className="text-xs text-primary-600 hover:underline"
              >
                {t('machines.showAll')}
              </button>
              <button
                onClick={() => onSet([])}
                className="text-xs text-primary-600 hover:underline"
              >
                {t('machines.hideAll')}
              </button>
            </div>
            <p className="px-3 pt-2 pb-1.5 text-xs font-medium text-gray-400">
              {t('machines.columnsBasic')}
            </p>
            {BASE_COLUMNS.map((column) => (
              <label
                key={column.key}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={visible.includes(column.key)}
                  onChange={() => onToggle(column.key)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {columnLabel(t, column)}
              </label>
            ))}
            <p className="px-3 pt-3 pb-1.5 text-xs font-medium text-gray-400 border-t border-gray-100 mt-2">
              {t('machines.columnsControlBox')}
            </p>
            {controlBoxColumns.map((column) => (
              <label
                key={column.key}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={visible.includes(column.key)}
                  onChange={() => onToggle(column.key)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {columnLabel(t, column)}
              </label>
            ))}
            <p className="px-3 pt-3 pb-1.5 text-xs font-medium text-gray-400 border-t border-gray-100 mt-2">
              {t('machines.columnsComponents')}
            </p>
            {otherColumns.map((column) => (
              <label
                key={column.key}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={visible.includes(column.key)}
                  onChange={() => onToggle(column.key)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {columnLabel(t, column)}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function columnLabel(
  t: ReturnType<typeof useTranslation>['t'],
  column: ColumnDef
): string {
  return column.label ?? t(column.labelKey!)
}

function sortRowsByColumn(
  rows: MachineRow[],
  columns: ColumnDef[],
  sortKey: string,
  sortAsc: boolean
): MachineRow[] {
  const column = columns.find((c) => c.key === sortKey) ?? columns[0]
  return [...rows].sort((a, b) => {
    const av = column.value(a)
    const bv = column.value(b)
    // 空値は昇順・降順にかかわらず末尾へ
    if (av === '' && bv === '') return 0
    if (av === '') return 1
    if (bv === '') return -1
    const cmp = av.localeCompare(bv, 'ja', { numeric: true })
    return sortAsc ? cmp : -cmp
  })
}

// 一覧のソート順を一括編集・CSVでも使えるように公開する
export function sortMachineRows(
  rows: MachineRow[],
  componentDefs: ComponentDef[],
  sortKey: string,
  sortAsc: boolean
): MachineRow[] {
  return sortRowsByColumn(rows, buildColumns(componentDefs), sortKey, sortAsc)
}

// CSV エクスポート（表示中の列に限らず全列を出力する）
export function machinesToCsv(
  t: ReturnType<typeof useTranslation>['t'],
  rows: MachineRow[],
  componentDefs: ComponentDef[]
): string {
  const columns = buildColumns(componentDefs)
  const escape = (value: string) =>
    /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
  const header = columns.map((column) => escape(columnLabel(t, column)))
  const lines = rows.map((row) =>
    columns.map((column) => escape(column.value(row))).join(',')
  )
  return [header.join(','), ...lines].join('\r\n')
}

export function saveVisibleColumns(
  keys: string[],
  allKeys: string[]
): string[] {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ visible: keys, known: allKeys })
  )
  return keys
}

export function toggleVisibleColumn(
  visible: string[],
  key: string,
  allKeys: string[]
): string[] {
  const next = visible.includes(key)
    ? visible.filter((k) => k !== key)
    : [...visible, key]
  return saveVisibleColumns(next, allKeys)
}

const MachineTable = forwardRef<MachineTableHandle, MachineTableProps>(
  function MachineTable(
    {
      rows,
      componentDefs,
      visibleColumns: visible,
      highlight,
      sortKey,
      sortAsc,
      onSortChange,
      onSelect,
      onPaint,
      onSelectionChange,
    },
    ref
  ) {
    const { t } = useTranslation()

    const columns = useMemo(() => buildColumns(componentDefs), [componentDefs])

    const tableRef = useRef<HTMLTableElement>(null)
    const [colWidths, setColWidths] =
      useState<Record<string, number>>(loadColWidths)
    // ドラッグ直後に th の click（ソート）が発火するのを抑止するフラグ
    const suppressSortRef = useRef(false)
    // 一度でも幅を変えた列があれば、スプレッドシート同様の固定レイアウトで描画する
    const fixedLayout = Object.keys(colWidths).length > 0

    // 全列オフにされた場合はシリアル列だけ残す
    const selected = columns.filter((c) => visible.includes(c.key))
    const visibleColumns =
      selected.length > 0
        ? selected
        : [columns.find((c) => c.key === 'serial') ?? columns[0]]

    // 固定列（シリアル〜タイプのうち表示中のもの。列順の先頭に並ぶ）と
    // その left 位置。列幅がドラッグで変わるため描画後の実測幅から毎回求める
    const frozenKeys = visibleColumns
      .filter((c) => FROZEN_COLUMN_KEYS.includes(c.key))
      .map((c) => c.key)
    // 最後の固定列にだけ、スクロール領域との境界の縦線を引く
    const lastFrozenKey = frozenKeys[frozenKeys.length - 1]
    const [stickyLefts, setStickyLefts] = useState<Record<string, number>>({})
    useLayoutEffect(() => {
      const table = tableRef.current
      if (!table) return
      const widths = new Map<string, number>()
      table
        .querySelectorAll<HTMLTableCellElement>('thead th[data-col]')
        .forEach((el) => widths.set(el.dataset.col!, el.offsetWidth))
      const next: Record<string, number> = {}
      let left = widths.get(DETAIL_COL_KEY) ?? 0
      for (const key of frozenKeys) {
        next[key] = left
        left += widths.get(key) ?? 0
      }
      setStickyLefts((prev) => {
        const keys = Object.keys(next)
        const same =
          keys.length === Object.keys(prev).length &&
          keys.every((k) => prev[k] === next[k])
        return same ? prev : next
      })
    })

    // 現在の描画幅を全列分スナップショットする。幅未指定の列があると
    // 固定レイアウト切り替え時にその列がガタつくため、必ず全列を埋める
    const snapshotWidths = (): Record<string, number> => {
      const widths: Record<string, number> = { ...colWidths }
      tableRef.current
        ?.querySelectorAll<HTMLTableCellElement>('thead th[data-col]')
        .forEach((el) => {
          const k = el.dataset.col!
          if (!(k in widths)) {
            widths[k] = Math.round(el.getBoundingClientRect().width)
          }
        })
      return widths
    }

    const saveColWidths = (next: Record<string, number>) => {
      setColWidths(next)
      localStorage.setItem(WIDTH_STORAGE_KEY, JSON.stringify(next))
    }

    // 列内容の自然幅（折り返しなし）を測る。固定レイアウトではセルが
    // 切り詰められているため、隠し要素に複製して max-content の幅を測る
    const measureFitWidth = (key: string): number => {
      const table = tableRef.current
      if (!table) return 0
      const measurer = document.createElement('div')
      measurer.style.position = 'absolute'
      measurer.style.visibility = 'hidden'
      measurer.style.width = 'max-content'
      table.parentElement?.appendChild(measurer)
      let width = MIN_COL_WIDTH
      table
        .querySelectorAll<HTMLTableCellElement>('th[data-col], td[data-col]')
        .forEach((cell) => {
          if (cell.dataset.col !== key) return
          const clone = cell.cloneNode(true) as HTMLElement
          // テーブル外に置いた td は幅計算が不安定なため inline-block で自然幅を測る
          clone.style.display = 'inline-block'
          clone.style.width = 'max-content'
          clone.style.maxWidth = 'none'
          measurer.appendChild(clone)
          width = Math.max(
            width,
            Math.ceil(clone.getBoundingClientRect().width)
          )
          measurer.removeChild(clone)
        })
      measurer.remove()
      return width + 1
    }

    // ダブルクリックで列幅をセル内容にフィットさせる
    const autoFitColumn = (key: string) => {
      const width = measureFitWidth(key)
      if (!width) return
      saveColWidths({
        ...snapshotWidths(),
        [key]: Math.min(width, MAX_FIT_WIDTH),
      })
    }

    // 詳細ボタン列の幅は保存値から復元しないため、固定レイアウトに入ったら
    // その時点の内容にフィットさせる（描画前に測って設定する）
    useLayoutEffect(() => {
      if (!fixedLayout || colWidths[DETAIL_COL_KEY] != null) return
      const width = measureFitWidth(DETAIL_COL_KEY)
      if (width) {
        setColWidths((prev) => ({ ...prev, [DETAIL_COL_KEY]: width }))
      }
    })

    const startResize = (
      e: React.PointerEvent<HTMLSpanElement>,
      key: string
    ) => {
      e.preventDefault()
      e.stopPropagation()
      if (!tableRef.current) return
      // 初回は全列の描画幅を固定してからドラッグした列だけ変える（他列が動かないように）
      const widths = snapshotWidths()
      const startX = e.clientX
      const startWidth = widths[key]
      const widthAt = (clientX: number) =>
        Math.max(MIN_COL_WIDTH, Math.round(startWidth + clientX - startX))
      suppressSortRef.current = true
      // ドラッグ中のイベントをハンドル要素に集める（マウス・タッチ共通）
      const handle = e.currentTarget
      handle.setPointerCapture(e.pointerId)
      const onMove = (ev: PointerEvent) => {
        setColWidths({ ...widths, [key]: widthAt(ev.clientX) })
      }
      const onUp = (ev: PointerEvent) => {
        handle.removeEventListener('pointermove', onMove)
        handle.removeEventListener('pointerup', onUp)
        handle.removeEventListener('pointercancel', onUp)
        document.body.style.removeProperty('cursor')
        saveColWidths({ ...widths, [key]: widthAt(ev.clientX) })
        setTimeout(() => {
          suppressSortRef.current = false
        })
      }
      document.body.style.cursor = 'col-resize'
      handle.addEventListener('pointermove', onMove)
      handle.addEventListener('pointerup', onUp)
      handle.addEventListener('pointercancel', onUp)
      setColWidths(widths)
    }

    const handleSort = (key: string) => {
      if (suppressSortRef.current) return
      onSortChange(key, sortKey === key ? !sortAsc : true)
    }

    const sortedRows = useMemo(
      () => sortRowsByColumn(rows, columns, sortKey, sortAsc),
      [rows, columns, sortKey, sortAsc]
    )

    // ---- セル選択（Excel風） ----
    // 選択はアンカー→フォーカスの矩形範囲の集まり。行キー・列キーで持ち、
    // 並び替えや列の表示切替のたびに現在の並びで矩形として解決し直す
    const [selection, setSelection] = useState<SelRange[]>([])
    // ドラッグによる範囲選択中かどうか（mouseenter で範囲を広げる）
    const draggingRef = useRef(false)

    const rowByKey = useMemo(
      () => new Map(sortedRows.map((row) => [row.key, row])),
      [sortedRows]
    )
    const rowIndexByKey = useMemo(
      () => new Map(sortedRows.map((row, index) => [row.key, index])),
      [sortedRows]
    )
    const colIndexByKey = useMemo(
      () => new Map(visibleColumns.map((column, index) => [column.key, index])),
      [visibleColumns]
    )

    // 範囲をセルの列挙に展開する（表示から消えた行・列を含む範囲は無視）
    const expandRanges = (ranges: SelRange[]): CellRef[] => {
      const seen = new Set<string>()
      const cells: CellRef[] = []
      for (const range of ranges) {
        const r1 = rowIndexByKey.get(range.anchor.rowKey)
        const r2 = rowIndexByKey.get(range.focus.rowKey)
        const c1 = colIndexByKey.get(range.anchor.colKey)
        const c2 = colIndexByKey.get(range.focus.colKey)
        if (
          r1 === undefined ||
          r2 === undefined ||
          c1 === undefined ||
          c2 === undefined
        ) {
          continue
        }
        for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
          for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
            const cell = {
              rowKey: sortedRows[r].key,
              colKey: visibleColumns[c].key,
            }
            const id = cellId(cell)
            if (!seen.has(id)) {
              seen.add(id)
              cells.push(cell)
            }
          }
        }
      }
      return cells
    }

    const selectionCells = expandRanges(selection)
    const selectedCells = new Set(selectionCells.map(cellId))
    const paintableCells = selectionCells.filter((cell) => {
      const row = rowByKey.get(cell.rowKey)
      return row?.machine || row?.device
    })

    // ツールバーの塗りつぶしボタンの活性制御用に対象セル数を通知する
    const paintableCount = paintableCells.length
    useEffect(() => {
      onSelectionChange?.(paintableCount)
    }, [paintableCount, onSelectionChange])

    // 左クリックで選択。Shift で最後の範囲を拡張、Ctrl/Cmd で範囲を追加（Excel風）
    const handleCellMouseDown = (
      e: React.MouseEvent,
      row: MachineRow,
      colKey: string
    ) => {
      if (e.button !== 0) return
      const cell = { rowKey: row.key, colKey }
      if (e.shiftKey && selection.length > 0) {
        setSelection((prev) =>
          prev.slice(0, -1).concat({ ...prev[prev.length - 1], focus: cell })
        )
      } else if (e.ctrlKey || e.metaKey) {
        setSelection((prev) => [...prev, { anchor: cell, focus: cell }])
      } else {
        setSelection([{ anchor: cell, focus: cell }])
      }
      draggingRef.current = true
    }

    // ドラッグ中に他のセルへ入ったら最後の範囲をそこまで広げる
    const handleCellMouseEnter = (row: MachineRow, colKey: string) => {
      if (!draggingRef.current) return
      const cell = { rowKey: row.key, colKey }
      setSelection((prev) =>
        prev.length > 0
          ? prev.slice(0, -1).concat({ ...prev[prev.length - 1], focus: cell })
          : prev
      )
    }

    useEffect(() => {
      const onMouseUp = () => {
        draggingRef.current = false
      }
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setSelection([])
          setColorMenu(null)
        }
      }
      window.addEventListener('mouseup', onMouseUp)
      window.addEventListener('keydown', onKeyDown)
      return () => {
        window.removeEventListener('mouseup', onMouseUp)
        window.removeEventListener('keydown', onKeyDown)
      }
    }, [])

    // ---- 右クリックの色付けメニュー ----
    // 開いた時点の選択セルを対象として保持する
    const [colorMenu, setColorMenu] = useState<{
      x: number
      y: number
      cells: CellRef[]
    } | null>(null)

    const openColorMenu = (
      e: React.MouseEvent,
      row: MachineRow,
      colKey: string
    ) => {
      if (!onPaint) return
      // 選択の外を右クリックしたときはそのセルだけの選択に切り替える（Excel風）
      const cell = { rowKey: row.key, colKey }
      let ranges = selection
      if (!selectedCells.has(cellId(cell))) {
        ranges = [{ anchor: cell, focus: cell }]
        setSelection(ranges)
      }
      const cells = expandRanges(ranges).filter((c) => {
        const target = rowByKey.get(c.rowKey)
        return target?.machine || target?.device
      })
      if (cells.length === 0) return
      e.preventDefault()
      setColorMenu({
        // 画面端で見切れないよう内側に寄せる（メニューの概寸 232x150px）
        x: Math.min(e.clientX, window.innerWidth - 244),
        y: Math.min(e.clientY, window.innerHeight - 162),
        cells,
      })
    }

    // 対象セルを機材/デバイスごとの更新へまとめて通知する（type='row' は行の色）
    const emitPaint = (
      cells: CellRef[],
      type: 'cell' | 'row',
      color: MachineColor | null
    ) => {
      if (!onPaint || cells.length === 0) return
      const value = color ?? ''
      const updates = new Map<string, PaintUpdate>()
      for (const cell of cells) {
        const row = rowByKey.get(cell.rowKey)
        const machine = row?.machine
        const deviceName = row?.device?.deviceName
        if (!machine && !deviceName) continue
        const key = machine ? machine.machineId : `device:${deviceName}`
        let update = updates.get(key)
        if (!update) {
          update = {
            ...(machine ? { machine } : { deviceName }),
            patch: type === 'row' ? { rowColor: value } : { cellColors: {} },
          }
          updates.set(key, update)
        }
        if (type === 'cell') update.patch.cellColors![cell.colKey] = value
      }
      onPaint([...updates.values()], { type, color })
    }

    const paintFromMenu = (
      type: 'cell' | 'row',
      color: MachineColor | null
    ) => {
      if (colorMenu) emitPaint(colorMenu.cells, type, color)
      setColorMenu(null)
    }

    // ツールバーの塗りつぶしボタンから現在の選択範囲へ色を付けられるようにする
    useImperativeHandle(ref, () => ({
      paintSelection: (type, color) => emitPaint(paintableCells, type, color),
    }))

    // メニュー表示用: 対象の行数と、単一セル/単一行選択時の現在色
    const menuRowKeys = colorMenu
      ? [...new Set(colorMenu.cells.map((cell) => cell.rowKey))]
      : []
    const menuSingleRow =
      menuRowKeys.length === 1 ? rowByKey.get(menuRowKeys[0]) : undefined
    const menuSingleColors = menuSingleRow
      ? rowColors(menuSingleRow)
      : undefined
    const menuCellCurrent =
      colorMenu?.cells.length === 1
        ? menuSingleColors?.cellColors?.[colorMenu.cells[0].colKey]
        : undefined

    return (
      <div>
        {rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">
            {t('machines.empty')}
          </div>
        ) : (
          <table
            ref={tableRef}
            // width が auto のままだと table-fixed が効かない（CSS仕様で自動レイアウトに
            // フォールバックする）ため、固定レイアウト時は w-full で幅を明示する
            className={`min-w-full divide-y divide-gray-200 ${
              fixedLayout ? 'w-full table-fixed' : ''
            }`}
          >
            {fixedLayout && (
              <colgroup>
                <col style={{ width: colWidths[DETAIL_COL_KEY] }} />
                {visibleColumns.map((column) => (
                  <col
                    key={column.key}
                    style={{
                      width: colWidths[column.key] ?? defaultColWidth(column),
                    }}
                  />
                ))}
              </colgroup>
            )}
            <thead className="bg-gray-50">
              <tr>
                {/* 詳細ボタン列（縦横スクロールしても左上に固定） */}
                <th
                  data-col={DETAIL_COL_KEY}
                  className="sticky top-0 left-0 z-20 bg-gray-50 shadow-[0_1px_0_0_#e5e7eb] px-2 py-2"
                />
                {visibleColumns.map((column) => (
                  <th
                    key={column.key}
                    data-col={column.key}
                    onClick={() => handleSort(column.key)}
                    // 固定列は横スクロールしても左に留める（left は実測幅から算出）
                    style={
                      stickyLefts[column.key] !== undefined
                        ? { left: stickyLefts[column.key] }
                        : undefined
                    }
                    // border-collapse では境界線がスクロールに追従しないため box-shadow で下線を引く
                    // （最後の固定列はスクロール領域との境界の縦線も内側に引く）
                    className={`sticky top-0 bg-gray-50 ${
                      stickyLefts[column.key] !== undefined ? 'z-20' : 'z-10'
                    } ${
                      column.key === lastFrozenKey
                        ? 'shadow-[inset_-1px_0_0_#e5e7eb,0_1px_0_0_#e5e7eb]'
                        : 'shadow-[0_1px_0_0_#e5e7eb]'
                    } px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap overflow-hidden`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {columnLabel(t, column)}
                      {sortKey === column.key ? (
                        sortAsc ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-300" />
                      )}
                    </span>
                    {/* 列幅ドラッグ用のハンドル（スプレッドシート風） */}
                    <span
                      onPointerDown={(e) => startResize(e, column.key)}
                      onDoubleClick={(e) => {
                        e.stopPropagation()
                        autoFitColumn(column.key)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none hover:bg-primary-300"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            {/* セル選択とテキスト選択が競合するため、本文のテキスト選択は無効にする */}
            <tbody className="divide-y divide-gray-200 select-none cursor-default">
              {sortedRows.map((row) => {
                const colors = rowColors(row)
                // 行マーカー色。色付きの行はホバー変化より色の維持を優先する
                const rowBg = colors?.rowColor
                  ? (COLOR_BG[colors.rowColor] ?? '')
                  : ''
                return (
                  <tr
                    key={row.key}
                    // 削除済みの機材は薄く表示する（「削除済みを表示」時のみ現れる）
                    className={`group ${rowBg || 'hover:bg-gray-50'} ${
                      row.machine?.deletedAt ? 'opacity-50' : ''
                    }`}
                  >
                    <td
                      data-col={DETAIL_COL_KEY}
                      // 横スクロールで下をくぐる内容を隠すため背景は不透明にする
                      className={`sticky left-0 ${
                        rowBg || 'bg-white group-hover:bg-gray-50'
                      } px-2 py-1.5 text-sm align-top`}
                    >
                      <button
                        onClick={() => onSelect(row)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                      >
                        {row.machine
                          ? t('machines.detail')
                          : t('machines.importToLedger')}
                      </button>
                      {row.machine?.deletedAt && (
                        <span className="mt-1 block w-fit px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap">
                          {t('detail.deletedBadge')}
                        </span>
                      )}
                    </td>
                    {visibleColumns.map((column) => {
                      // 固定レイアウト時は列幅より長い内容をはみ出させない
                      const clip = fixedLayout ? ' overflow-hidden' : ''
                      // セルマーカー色（行マーカー色より優先して表示される）
                      const cellColor = colors?.cellColors?.[column.key]
                      const cellBg = cellColor
                        ? ` ${COLOR_BG[cellColor] ?? ''}`
                        : ''
                      // 選択中のセルは内側の枠線で強調する
                      const selRing = selectedCells.has(
                        cellId({ rowKey: row.key, colKey: column.key })
                      )
                        ? ' ring-2 ring-inset ring-primary-500'
                        : ''
                      const cellHandlers = {
                        onMouseDown: (e: React.MouseEvent) =>
                          handleCellMouseDown(e, row, column.key),
                        onMouseEnter: () =>
                          handleCellMouseEnter(row, column.key),
                        onContextMenu: (e: React.MouseEvent) =>
                          openColorMenu(e, row, column.key),
                      }
                      // 固定列は sticky + 不透明背景で、横スクロール時に
                      // 下をくぐる内容を隠す（マーカー色があればそれを優先）
                      const frozenLeft = stickyLefts[column.key]
                      const frozenStyle =
                        frozenLeft !== undefined
                          ? { left: frozenLeft }
                          : undefined
                      const frozenClass =
                        frozenLeft !== undefined
                          ? ` sticky ${
                              cellColor
                                ? ''
                                : rowBg || 'bg-white group-hover:bg-gray-50'
                            }${
                              column.key === lastFrozenKey
                                ? ' shadow-[inset_-1px_0_0_#e5e7eb]'
                                : ''
                            }`
                          : ''
                      if (column.key === 'serial') {
                        const serial = column.value(row)
                        return (
                          <td
                            key={column.key}
                            data-col={column.key}
                            {...cellHandlers}
                            style={frozenStyle}
                            className={`px-4 py-1.5 text-sm font-medium text-primary-700 align-top whitespace-nowrap${clip}${cellBg}${selRing}${frozenClass}`}
                          >
                            {serial ? (
                              <Highlight text={serial} query={highlight} />
                            ) : (
                              '-'
                            )}
                          </td>
                        )
                      }
                      if (column.key === 'certificate') {
                        return (
                          <td
                            key={column.key}
                            data-col={column.key}
                            {...cellHandlers}
                            className={`px-4 py-1.5 text-sm align-top${clip}${cellBg}${selRing}`}
                          >
                            <CertBadge row={row} />
                          </td>
                        )
                      }
                      // リンク列はクリックで別タブに開けるアンカーとして表示する
                      if (LINK_COLUMN_KEYS.has(column.key)) {
                        const url = column.value(row)
                        return (
                          <td
                            key={column.key}
                            data-col={column.key}
                            {...cellHandlers}
                            style={frozenStyle}
                            className={`px-4 py-1.5 text-sm text-gray-600 align-top whitespace-nowrap${clip}${cellBg}${selRing}${frozenClass}`}
                          >
                            {url ? (
                              isHttpUrl(url) ? (
                                // URLの文字列は表示せず、遷移アイコンだけ置く
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={url}
                                  // クリックがセル選択のドラッグ開始にならないようにする
                                  onMouseDown={(e) => e.stopPropagation()}
                                  className="inline-flex p-0.5 text-primary-600 hover:text-primary-800"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              ) : (
                                <Highlight text={url} query={highlight} />
                              )
                            ) : (
                              '-'
                            )}
                          </td>
                        )
                      }
                      const value = column.value(row)
                      return (
                        <td
                          key={column.key}
                          data-col={column.key}
                          {...cellHandlers}
                          style={frozenStyle}
                          className={`px-4 py-1.5 text-sm text-gray-600 align-top ${
                            column.longText
                              ? 'break-words min-w-[14rem] max-w-[24rem]'
                              : 'whitespace-nowrap'
                          }${clip}${cellBg}${selRing}${frozenClass}`}
                        >
                          {value ? (
                            column.longText ? (
                              <MarkdownView
                                value={value}
                                highlight={highlight}
                              />
                            ) : (
                              <Highlight text={value} query={highlight} />
                            )
                          ) : (
                            '-'
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {/* 右クリックの色付けメニュー（Excel のセル塗りに相当。全ユーザー共有） */}
        {colorMenu && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setColorMenu(null)}
              onContextMenu={(e) => {
                e.preventDefault()
                setColorMenu(null)
              }}
            />
            <div
              className="fixed z-40 w-max bg-white border border-gray-200 rounded-md shadow-lg py-2"
              style={{ left: colorMenu.x, top: colorMenu.y }}
            >
              <p className="px-3 pb-1 text-xs font-medium text-gray-400">
                {t('machines.cellColor')}
                {colorMenu.cells.length > 1
                  ? ` (${colorMenu.cells.length})`
                  : ''}
              </p>
              <ColorSwatches
                current={menuCellCurrent}
                clearLabel={t('machines.clearColor')}
                onPick={(color) => paintFromMenu('cell', color)}
              />
              <p className="px-3 pt-2 pb-1 mt-1 text-xs font-medium text-gray-400 border-t border-gray-100">
                {t('machines.rowColor')}
                {menuRowKeys.length > 1 ? ` (${menuRowKeys.length})` : ''}
              </p>
              <ColorSwatches
                current={menuSingleColors?.rowColor}
                clearLabel={t('machines.clearColor')}
                onPick={(color) => paintFromMenu('row', color)}
              />
            </div>
          </>
        )}
      </div>
    )
  }
)

export default MachineTable

// ツールバーに置く塗りつぶしボタン（Excel の「塗りつぶしの色」相当）。
// 本体クリックで前回の色を選択範囲へ再適用、▼でパレットを開く
export function PaintButton({
  disabled,
  lastPaint,
  onPick,
}: {
  disabled: boolean
  // 前回適用した色。null 始まり（未適用）のときは本体クリックでもパレットを開く
  lastPaint: PaintAction | null
  onPick: (type: 'cell' | 'row', color: MachineColor | null) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const pick = (type: 'cell' | 'row', color: MachineColor | null) => {
    setOpen(false)
    onPick(type, color)
  }

  return (
    <div className="relative">
      <div
        className={`inline-flex items-stretch bg-white border border-gray-300 rounded-md ${
          disabled ? 'opacity-50' : ''
        }`}
      >
        <button
          onClick={() =>
            lastPaint ? onPick(lastPaint.type, lastPaint.color) : setOpen(true)
          }
          disabled={disabled}
          title={t('machines.paint')}
          className="flex flex-col items-center justify-center gap-0.5 pl-3 pr-2 py-1.5 text-gray-700 rounded-l-md enabled:hover:bg-gray-50 transition-colors"
        >
          <PaintBucket className="h-4 w-4" />
          {/* Excel 同様、前回の色をアイコン下のバーで示す（色消しは白） */}
          <span
            className={`block h-1 w-4 rounded-sm ${
              lastPaint?.color
                ? COLOR_SWATCH[lastPaint.color]
                : 'bg-white border border-gray-300'
            }`}
          />
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          title={t('machines.paint')}
          className="flex items-center px-1 text-gray-500 border-l border-gray-200 rounded-r-md enabled:hover:bg-gray-50 transition-colors"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-20 w-max bg-white border border-gray-200 rounded-md shadow-lg py-2">
            <p className="px-3 pb-1 text-xs font-medium text-gray-400">
              {t('machines.cellColor')}
            </p>
            <ColorSwatches
              clearLabel={t('machines.clearColor')}
              onPick={(color) => pick('cell', color)}
            />
            <p className="px-3 pt-2 pb-1 mt-1 text-xs font-medium text-gray-400 border-t border-gray-100">
              {t('machines.rowColor')}
            </p>
            <ColorSwatches
              clearLabel={t('machines.clearColor')}
              onPick={(color) => pick('row', color)}
            />
          </div>
        </>
      )}
    </div>
  )
}
